import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Container,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Article, ArrowForward, Person, CalendarToday } from "@mui/icons-material";
import { getApiUrl } from "../config/api";

export const Ministracoes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [ministracoes, setMinistracoes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMinistracoes = async () => {
      try {
        const response = await fetch(getApiUrl("/api/ministracoes"));
        const data = await response.json();
        if (data.success) {
          setMinistracoes(data.data);
        }
      } catch (error) {
        console.error("Erro ao buscar ministrações:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMinistracoes();
  }, []);

  const handleCardClick = (id) => {
    navigate(`/ministracoes/${id}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  };

  return (
    <Box 
      sx={{ 
        backgroundColor: "#f8f9fa", 
        minHeight: "100vh",
        pt: { xs: "80px", md: "100px" },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        {/* Cabeçalho */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              mb: 2,
              fontSize: { xs: "2rem", md: "2.5rem" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Article sx={{ fontSize: { xs: 32, md: 40 } }} />
            Ministrações
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1rem", md: "1.1rem" },
              lineHeight: 1.6,
              px: { xs: 2, md: 0 },
            }}
          >
            Palavras de edificação, ensino e crescimento espiritual para fortalecer sua caminhada com Deus.
          </Typography>
        </Box>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "primary.main" }} />
          </Box>
        )}

        {/* Grid de Ministrações */}
        <Grid container spacing={{ xs: 1, md: 2 }} sx={{ justifyContent: "center" }}>
          {ministracoes.map((ministracao) => (
            <Grid item xs={12} sm={6} md={4} key={ministracao.id}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  border: "1px solid #e0e0e0",
                  borderRadius: 3,
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  maxWidth: "320px",
                  mx: "auto",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: `0 8px 30px ${theme.palette.primary.main}`,
                  },
                }}
                onClick={() => handleCardClick(ministracao.id)}
              >
                {/* Imagem */}
                <CardMedia
                  component="img"
                  height={isMobile ? "200" : "220"}
                  image={ministracao.imagem}
                  alt={ministracao.titulo}
                  sx={{
                    objectFit: "cover",
                    backgroundColor: "#f0f0f0",
                  }}
                  onError={(e) => {
                    e.target.src = `data:image/svg+xml;base64,${btoa(`
                      <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
                        <rect width="100%" height="100%" fill="${theme.palette.primary.main}"/>
                        <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="16" 
                              fill="white" text-anchor="middle" dy="0.3em">
                          Imagem não encontrada
                        </text>
                      </svg>
                    `)}`;
                  }}
                />
                
                {/* Conteúdo */}
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  {/* Meta informações */}
                  <Box 
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 2, 
                      mb: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 16, color: "primary.main" }} />
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {formatDate(ministracao.data)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Person sx={{ fontSize: 16, color: "primary.main" }} />
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {ministracao.autor || "Igreja Verbo da Vida"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Título */}
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "bold",
                      color: "#333",
                      mb: 1.5,
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minHeight: "2.6em",
                    }}
                  >
                    {ministracao.titulo}
                  </Typography>

                  {/* Resumo */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 3,
                      lineHeight: 1.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minHeight: "4.5em",
                    }}
                  >
                    {ministracao.conteudo}
                  </Typography>

                  {/* Link "Leia mais" */}
                  <Button
                    endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                    sx={{
                      color: "primary.main",
                      fontWeight: "bold",
                      textTransform: "none",
                      p: 0,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "primary.dark",
                      },
                    }}
                  >
                    Leia mais
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Mensagem se não houver ministrações */}
        {ministracoes.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Nenhuma ministração disponível no momento.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};